// @ts-strict-ignore
import * as React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectBuildDistributionDownloadMetricsByBucketId } from '@/selectors/selectors';
import { BuildDistroBuild } from '@/types';
import { Column } from '@ant-design/charts';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { useChartColors, useChartTheme } from '../styles/themeMode';
import { InfoCircleOutlined } from '@ant-design/icons';
import Tooltip from 'antd/lib/tooltip';
import { Skeleton } from 'antd/lib';
import Typography from 'antd/lib/typography';

interface BucketDownloadMetricsContainerProps extends AppComponentProps {
    bucketId: string;
    isLoading?: boolean;
}

export const BucketDownloadMetricsContainer: React.FC<BucketDownloadMetricsContainerProps> = (
    props: BucketDownloadMetricsContainerProps,
) => {
    const chartTheme = useChartTheme();
    const chartColors = useChartColors();
    const { bucketId, isLoading } = props;

    const metrics = useSelector(
        (state: RootState) =>
            selectBuildDistributionDownloadMetricsByBucketId(state, bucketId)?.numDownloadsPerBuild || {},
    );
    const buildsById = useSelector(
        (state: RootState) => state.entities.buildDistribution.buildsById as Record<string, BuildDistroBuild>,
    );

    const { chartData } = React.useMemo(() => {
        const data = Object.entries(metrics).map(([buildId, value]) => {
            return {
                build: buildId,
                value,
            };
        });

        return {
            chartData: data,
        };
    }, [metrics, buildsById]);

    const formatBuildLabel = (buildId: string) => {
        return /^\d+$/.test(buildId) ? `#${buildId}` : buildId;
    };

    const chartProps = React.useMemo(
        () => ({
            data: chartData,
            xField: 'build',
            yField: 'value',
            appendPadding: [20, 20, 6, 20],
            color: runwayColors.primary,
            animation: false,
            meta: {
                value: {
                    alias: 'Total downloads',
                    color: 'transparent',
                },
                build: {
                    formatter: formatBuildLabel,
                },
            },
            label: null,
            legend: {
                offsetY: 20,
            },
            tooltip: {
                domStyles: {
                    'g2-tooltip': {
                        backgroundColor: chartColors.tooltipBg,
                        color: chartColors.tooltipText,
                    },
                },
                formatter: (datum: { build: string; value: number }) => ({
                    name: formatBuildLabel(datum.build),
                    value: datum.value,
                }),
            },
        }),
        [chartData, formatBuildLabel, chartColors],
    );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: 256,
                border: '1px solid',
                borderColor: antGrey.grey4,
                borderRadius: 8,
                gridColumn: 'auto / span 3',
                alignItems: 'stretch',
                position: 'relative',
            }}
        >
            {isLoading ?
                <Skeleton
                    active
                    round={false}
                    style={{
                        padding: 12,
                    }}
                    paragraph={{
                        rows: 3,
                    }}
                />
            :   <>
                    <div
                        style={{
                            height: 56,
                        }}
                    >
                        <div
                            style={{
                                padding: '16px 20px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                height: '100%',
                                position: 'relative',
                                gap: 4,
                            }}
                        >
                            <div
                                style={{
                                    height: 22,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 7,
                                    alignItems: 'center',
                                }}
                            >
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey8,
                                        fontWeight: 600,
                                        fontSize: 14,
                                        maxWidth: 200,
                                        marginBottom: 0,
                                    }}
                                    ellipsis={{
                                        tooltip: 'Number of build downloads',
                                    }}
                                >
                                    Number of build downloads
                                </Typography.Text>
                                <Tooltip title="Number of times each build was downloaded in the selected time range.">
                                    <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            flex: 1,
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            height: 200,
                        }}
                    >
                        <Column {...chartProps} theme={chartTheme} />
                    </div>
                </>
            }
        </div>
    );
};
